import { Box, Typography, Stepper, Step, StepLabel, StepContent, Card, CardContent } from "@mui/material";
import dayjs from "dayjs";

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
const dateTimePattern = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const istDateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
	timeZone: "Asia/Kolkata",
	day: "2-digit",
	month: "short",
	year: "numeric",
	hour: "2-digit",
	minute: "2-digit",
	hour12: true,
});
const istDateFormatter = new Intl.DateTimeFormat("en-IN", {
	timeZone: "Asia/Kolkata",
	day: "2-digit",
	month: "short",
	year: "numeric",
});

const normalizeDateValue = (value) => (typeof value === "string" ? value.replace(" ", "T") : value);

const formatDateTime = (value) => {
	if (!value) return "-";
	const normalizedValue = normalizeDateValue(value);
	const date = new Date(normalizedValue);
	if (Number.isNaN(date.getTime())) return String(value);
	return istDateTimeFormatter.format(date).replace(",", " •");
};

const excludedKeys = ["id", "i_code", "createdAt", "updatedAt", "last_updated_by"];
const dateOnlyFields = new Set([
	"planned_sample_date",
	"sample_ready_date",
	"expected_production_inward",
	"sample_inward_date",
]);

const convertKeyName = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (x) => x.toUpperCase());
const isDateLikeString = (val) =>
	typeof val === "string" && dateTimePattern.test(val) && dayjs(normalizeDateValue(val)).isValid();

const formatDateOnlyValue = (value) => {
	const normalizedValue = normalizeDateValue(value);
	const date = new Date(normalizedValue);
	if (Number.isNaN(date.getTime())) return String(value);
	return istDateFormatter.format(date);
};

const formatFieldValue = (key, val) => {
	if (val === null || val === "") return null;
	if (dateOnlyFields.has(key) && isDateLikeString(val)) {
		return formatDateOnlyValue(val);
	}
	if (typeof val === "string" && dateOnlyPattern.test(val) && dayjs(val).isValid()) {
		return formatDateOnlyValue(`${val}T00:00:00`);
	}
	return isDateLikeString(val) ? formatDateTime(val) : val;
};

const getChanges = (entry, previousEntry, allowedKeys) => {
	const out = [];
	Object.keys(entry).forEach((key) => {
		if (excludedKeys.includes(key)) return;
		if (allowedKeys?.length && !allowedKeys.includes(key)) return;
		const val = entry[key];
		const formatted = formatFieldValue(key, val);
		if (formatted === null) return;

		const previousFormatted = formatFieldValue(key, previousEntry?.[key]);
		if (previousEntry && previousFormatted === formatted) return;

		out.push({
			label: convertKeyName(key),
			value: formatted,
		});
	});
	return out;
};

const HistoryVerticalStepper = ({ steps = [], allowedKeys = [] }) => {
	const sorted = [...(steps || [])].sort((a, b) => {
		const da = new Date(a.updatedAt || a.createdAt);
		const db = new Date(b.updatedAt || b.createdAt);
		return db - da;
	});

	const stepsWithChanges = sorted
		.map((step, idx) => ({
			step,
			changes: getChanges(step, sorted[idx + 1], allowedKeys),
		}))
		.filter((item) => item.changes.length > 0);

	if (!stepsWithChanges.length) {
		return (
			<Typography textAlign="center" color="gray" p={2}>
				No updates found
			</Typography>
		);
	}

	return (
		<Box width="100%">
			<Stepper orientation="vertical">
				{stepsWithChanges.map(({ step, changes }, idx) => {
					return (
						<Step key={idx} active>
							<StepLabel
								icon={<div />}
								sx={{
									"& .MuiStepIcon-root": { display: "none" },
								}}>
								<Typography fontWeight={600} fontSize={15}>
									{formatDateTime(step.updatedAt || step.createdAt)}
								</Typography>
							</StepLabel>

							<StepContent>
								<Box pl={1} mb={2}>
									<Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
										<CardContent sx={{ p: 2 }}>
											{changes.map((field, idx2) => (
												<Box key={idx2} mb={1.2}>
													<Typography variant="body2" fontWeight={600} color="primary.main">
														{field.label}
													</Typography>

													<Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
														{field.value}
													</Typography>
												</Box>
											))}

											<Typography variant="caption" color="gray">
												Updated By: {step.last_updated_by}
											</Typography>
										</CardContent>
									</Card>
								</Box>
							</StepContent>
						</Step>
					);
				})}
			</Stepper>
		</Box>
	);
};

export default HistoryVerticalStepper;
